// Patient chat — the front desk <-> treatment room thread. ONE thread per
// PATIENT (never per chart or per visit: a patient with three visits this year
// would otherwise leave you wondering which of three threads holds the answer).
//
// Backend: Xano `claude` group — tables chat_message / chat_read, functions
// chatSend / chatThread / chatUnread, proxied at /api/chat*.
// Full design + the reasoning: `docs/patient-chat-spec.md`.
//
// This module owns THREE things and nothing else:
//   · the fetchers
//   · the shared unread store (the sidebar badge and the toaster must never
//     disagree about the same number, so there is exactly one copy of it)
//   · the small pure helpers the dock and the toast both need
import { useEffect, useRef, useState } from 'react'
import { apiGet, postJson, apiDelete } from './api.js'
import { bkk, tsOf } from './datetime.js'
import { useXanoRealtime } from './realtime.js'

/* ── fetchers ─────────────────────────────────────────────────────────────── */

// Page 1 is the NEWEST `per_page` messages (Xano sorts id desc); the dock
// reverses for display and asks for page 2 to load older.
export function fetchThread(patientId, { page = 1, perPage = 50, signal } = {}) {
  return apiGet('/api/chat', { patient_id: patientId, page, per_page: perPage }, { signal })
}

// `treatment_id` is a CLAIM, not a fact — Xano validates it against the patient
// and silently drops it if it belongs to someone else.
export function sendMessage({ patientId, treatmentId = 0, body }) {
  return postJson('/api/chat', { patient_id: patientId, treatment_id: treatmentId || 0, body })
}

// Soft delete: the row survives as a tombstone and the server blanks the text.
export const deleteMessage = (id) => apiDelete(`/api/chat/${id}`)

// Returns the refreshed unread summary in the SAME response, so the badge can
// never lag the panel that just cleared it.
export const markRead = (patientId) => postJson('/api/chat/read', { patient_id: patientId })

export const fetchUnread = (days = 7) => apiGet('/api/chat/unread', { days })

/* ── the shared unread store ──────────────────────────────────────────────── */

const EMPTY = { threads: [], total_unread: 0, thread_count: 0, truncated: false }
let _unread = EMPTY
const _subs = new Set()
const _emit = () => { for (const fn of _subs) fn() }

export function setUnread(next) {
  if (!next || typeof next !== 'object') return
  _unread = {
    threads: Array.isArray(next.threads) ? next.threads : [],
    total_unread: Number(next.total_unread) || 0,
    thread_count: Number(next.thread_count) || 0,
    truncated: next.truncated === true,
  }
  _emit()
}

let _inflight = null
export async function refreshUnread(days = 7) {
  // Collapse concurrent refreshes — a burst of inbox events must not become a
  // burst of identical requests.
  if (_inflight) return _inflight
  _inflight = fetchUnread(days)
    .then((r) => { setUnread(r); return _unread })
    .catch(() => _unread)
    .finally(() => { _inflight = null })
  return _inflight
}

/**
 * Subscribe to the shared unread summary.
 *
 * `autoLoad` fetches once on mount. Only ONE component should pass it (the
 * toaster), or every badge on the page kicks its own first request.
 */
export function useUnread({ autoLoad = false } = {}) {
  const [, force] = useState(0)
  useEffect(() => {
    const rerender = () => force((n) => n + 1)
    _subs.add(rerender)
    if (autoLoad) refreshUnread()
    return () => { _subs.delete(rerender) }
  }, [autoLoad])
  return _unread
}

/** Drop one thread's unread locally, so the badge clears the instant it is read. */
export function clearThreadLocally(patientId) {
  const pid = String(patientId)
  const threads = _unread.threads.filter((t) => String(t.patient_id) !== pid)
  if (threads.length === _unread.threads.length) return
  const removed = _unread.threads.find((t) => String(t.patient_id) === pid)
  setUnread({
    threads,
    total_unread: Math.max(0, _unread.total_unread - (Number(removed?.unread) || 0)),
    thread_count: threads.length,
    truncated: _unread.truncated,
  })
}

/* ── the inbox channel ────────────────────────────────────────────────────── */

/**
 * `chat_inbox/{user_id}` — the per-user fan-out that makes this feature work at
 * all. A message lands on a patient page the recipient is not looking at, so
 * without this it is a message board people forget to check.
 *
 * Xano computes the recipients (everyone who has opened or posted in the thread,
 * plus the visit's doctor) and emits once per recipient, so anything arriving
 * here is already meant for this user. Self-echo is still dropped: the author's
 * own tab appended it optimistically.
 */
export function useChatInbox(me, onMessage) {
  const cb = useRef(onMessage)
  cb.current = onMessage
  const myId = me?.xano_id ? String(me.xano_id) : ''

  useXanoRealtime(myId ? `chat_inbox/${myId}` : null, (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    if (!p || typeof p !== 'object' || p.type !== 'chat_message') return
    if (myId && String(p.user_id) === myId) return
    refreshUnread()
    cb.current?.(p)
  })
}

/* ── pure helpers ─────────────────────────────────────────────────────────── */

export const isMine = (m, me) => !!me?.xano_id && String(m?.user_id) === String(me.xano_id)
export const isDeleted = (m) => m?.active === false

/**
 * Who has read `m`, excluding its author.
 *
 * Derived from chat_read (`last_read_at >= m.created_at`) rather than stored per
 * message — the front desk's real question is only ever "did the doctor see
 * it", and one row per (user, thread) answers that without rewriting every
 * message row on every open.
 */
export function readersOf(m, readers) {
  const at = tsOf(m?.created_at)
  if (!at) return []
  return (Array.isArray(readers) ? readers : [])
    .filter((r) => String(r.user_id) !== String(m.user_id))
    .filter((r) => tsOf(r.last_read_at) >= at)
    .map((r) => String(r.name || '').trim() || `#${r.user_id}`)
}

/** "HH:MM" in Bangkok time. */
export const chatTime = (v) => {
  const n = tsOf(v)
  if (!n) return ''
  return new Date(n).toLocaleTimeString('th-TH', { ...bkk, hour: '2-digit', minute: '2-digit' })
}

/** Day heading: วันนี้ / เมื่อวาน / d MMM yyyy — Bangkok days, not the browser's. */
const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const bkkDay = (v) => {
  const n = tsOf(v)
  return n ? new Date(n).toLocaleDateString('en-CA', bkk) : ''
}
export function dayLabel(isoDay) {
  if (!isoDay) return ''
  const today = new Date().toLocaleDateString('en-CA', bkk)
  if (isoDay === today) return 'วันนี้'
  const y = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', bkk)
  if (isoDay === y) return 'เมื่อวาน'
  const [yy, mm, dd] = isoDay.split('-')
  return `${Number(dd)} ${THAI_MONTHS[Number(mm) - 1]} ${yy}`
}

/**
 * Inbox timestamp: HH:MM for today, d/m for anything older. A bare HH:MM on a
 * three-day-old thread reads as "just now", which is the one thing an unread
 * list must not imply.
 */
export function chatWhen(v) {
  const n = tsOf(v)
  if (!n) return ''
  const d = new Date(n)
  if (d.toLocaleDateString('en-CA', bkk) === new Date().toLocaleDateString('en-CA', bkk)) return chatTime(n)
  const iso = d.toLocaleDateString('en-CA', bkk)
  const [, mm, dd] = iso.split('-')
  return `${Number(dd)}/${Number(mm)}`
}

/** One-line preview for the inbox list and the toast. */
export const snippet = (s, max = 90) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim()
  return t.length > max ? `${t.slice(0, max)}…` : t
}
